import Router from 'next/router';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { type OpenAssetModalLocations, useTrackOpenedAssetModal } from '@air/analytics';
import { ClipType } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { calculateRankForItem } from '~/analytics/rankUtils';
import { EXIT_COMPARISON_QUERY } from '~/constants/assetComparison';
import { QueryParamNames, type QueryType } from '~/constants/search';
import { centralizedBoardSelector } from '~/store/centralizedBoard/selectors';
import { setCentralizedClipAction } from '~/store/centralizedClip/actions';
import { toCentralizedClipBoard } from '~/store/centralizedClip/toCentralizedClipBoard';
import { getClipIdFromPath, pushWithExistingQuery } from '~/utils/PathUtils';
import { useAirStore } from '~/utils/ReduxUtils';

/**
 * Private-area owner of asset-page navigation.
 *
 * This is a deliberate, verbatim copy of the public twin (`useGoToPublicAssetPage`)
 * per rules/area-isolation.md: URL schemes are per-area architecture, so each area
 * owns its own navigation + path-building rather than sharing an app-generic hook.
 * The contracts are identical today and free to diverge later — do not re-fuse them.
 */
export type GoToPrivateAssetPageProps = {
  asset: Parameters<typeof setCentralizedClipAction>[0]['clip'];
  base?: string;
  query?: QueryType;
  trackLocation?: OpenAssetModalLocations;
  isAssetModalAlreadyOpen?: boolean;
};

const getQueryParamTimestamp = (asset: GoToPrivateAssetPageProps['asset']): string | null =>
  typeof asset.assets?.spriteSheet !== 'undefined' &&
  asset.type === ClipType.video &&
  asset.position?.start.timestamp !== undefined
    ? String(asset.position.start.timestamp)
    : null;

type PrivateAssetNavigation = ReturnType<typeof pushWithExistingQuery>;

const settlePrivateAssetNavigation = (
  navigation: PrivateAssetNavigation,
  resolve: () => void,
  reject: (reason?: unknown) => void,
) => {
  Promise.resolve(navigation).then((didNavigate) => {
    if (didNavigate === false) {
      reject(new Error('Private asset navigation was canceled'));
      return;
    }

    resolve();
  }, reject);
};

export const useGoToPrivateAssetPage = () => {
  const dispatch = useDispatch();
  const store = useAirStore();
  const { trackOpenedAssetModal } = useTrackOpenedAssetModal();

  const goToAssetPageAndWait = useCallback(
    ({
      asset,
      query,
      base = Router.asPath,
      trackLocation = 'not-set',
      isAssetModalAlreadyOpen = false,
    }: GoToPrivateAssetPageProps) => {
      return new Promise<void>((resolve, reject) => {
        Router.ready(() => {
          try {
            const basePath = base.split('?')[0].split('/c/')[0];
            const assetPath = `/c/${asset.id}`;
            const path = `${basePath}${assetPath}`;

            // Do not default-clear am-tab. Next/prev, version switch, and other in-modal clip
            // changes copy the current query and should keep Comments. Callers that want Info
            // (or a specific tab) pass amTab explicitly; PathUtils drops nulls from the href.
            const navigationQuery = {
              [QueryParamNames.notificationsPanel]: null,
              [QueryParamNames.timestamp]: getQueryParamTimestamp(asset),
              [QueryParamNames.discussionId]: null,
              [QueryParamNames.page]: null,
              [QueryParamNames.faceId]: null,
              ...EXIT_COMPARISON_QUERY,
              ...query,
            };

            // Query params can contain other clip ids (for example `?cmp=<clipId>`).
            // Only the `/c/<clipId>` route segment determines which asset is already open.
            if (getClipIdFromPath(Router.asPath) === asset.id) {
              if (query) {
                // Same clip: replace query on the live route. `base` is only for opening a
                // different clip — passing `path` here would rewrite a board URL to `/all/c/…`
                // (comment notifications and ads "go to asset" both pass `base: Routes.media.all`).
                const navigation = pushWithExistingQuery({
                  method: 'replace',
                  newQuery: query,
                });

                settlePrivateAssetNavigation(navigation, resolve, reject);
                return;
              }

              resolve();
              return;
            }

            // Id-only stubs (`{ id }`) merge onto `initialState.clip` defaults (`status: created`)
            // and flash the visualizer processing UI. `PrivateAssetModal`'s clip query owns the
            // Redux update after the URL changes.
            if (asset.status !== undefined) {
              const board = centralizedBoardSelector(store.getState());

              dispatch(
                setCentralizedClipAction({
                  clip: {
                    boards: board ? [toCentralizedClipBoard(board)] : [],
                    ...asset,
                  },
                }),
              );
            }

            const navigation = pushWithExistingQuery({
              path,
              newQuery: navigationQuery,
            });

            if (!isAssetModalAlreadyOpen) {
              const rank = calculateRankForItem(store, asset.id);

              trackOpenedAssetModal({
                location: trackLocation,
                ...(rank !== undefined && { rank }),
              });
            }

            settlePrivateAssetNavigation(navigation, resolve, reject);
          } catch (error) {
            reject(error);
          }
        });
      });
    },
    [dispatch, store, trackOpenedAssetModal],
  );

  const goToAssetPage = useCallback(
    (props: GoToPrivateAssetPageProps) => {
      void goToAssetPageAndWait(props).catch((error: unknown) => {
        reportErrorToBugsnag({ error, context: 'Failed to navigate to private asset' });
      });
    },
    [goToAssetPageAndWait],
  );

  return {
    goToAssetPage,
    goToAssetPageAndWait,
  };
};
